import React from "react";
import Flex from "../../../../structure/Flex";
import { DrawMap } from "../../../Maps";
import Checkboxes from "../Checkboxes";
import DateTimePicker from "../DateTimePicker";
import FileUpload from "../FileUpload";
import Input from "../Input";
import MobileInput from "../MobileInput";
import MultiSelect from "../MultiSelect";
import RadioButtons from "../RadioButtons";
import Select from "../Select";
import Textarea from "../Textarea";
import ToggleSwitch from "../ToggleSwitch";
import WSYIWYGEditor from "../WSYIWYGEditor";
import CountrySelector from "../CountrySelector";

type FormItems =
  | "text"
  | "select"
  | "number"
  | "email"
  | "mobile"
  | "textarea"
  | "wysiwyg"
  | "checkbox"
  | "radio"
  | "date"
  | "time"
  | "datetime"
  | "multi-select"
  | "file-upload"
  | "map"
  | "toggle"
  | "custom"
  | "country-selector";

interface FormElementProps {
  className?: string;
  name: string;
  label: string;
  type: FormItems;
  placeholder?: string;
  multiSelect?: boolean;
  options?: Array<{ id: any; value: any }>;
  value?: any;
  setValue?: React.Dispatch<any>;
  labelTopAligned?: boolean;
  labelWidthClass?: string;
  required?: boolean;
  hint?: string;
  fileAccept?: string;
  disabled?: boolean;
  map?: {
    heightClass?: string;
    widthClass?: string;
    options?: {
      center?: [number, number];
      zoom?: number;
      style?: string;
    };
    drawControls: {
      point?: boolean;
      line_string?: boolean;
      polygon?: boolean;
      combine_features?: boolean;
      uncombine_features?: boolean;
      trash?: boolean;
    };
    multiple?: true;
  };
  isSortable?: boolean;
  hideSelected?: boolean;
  isValidDate?: (current: Date) => boolean;
  handleQuerySubmit?: any;
  handleUpload?: any;
  min?: number;
  max?: number;
}

const FormElement: React.FC<FormElementProps> = ({
  className = "m-4",
  name,
  label,
  value,
  setValue,
  type,
  options,
  children,
  required,
  placeholder,
  labelTopAligned,
  fileAccept,
  labelWidthClass,
  hint,
  disabled,
  map = {
    heightClass: "h-72",
    widthClass: "w-full",
    options: {},
    drawControls: {},
    multiple: false,
  },
  isSortable,
  hideSelected,
  isValidDate,
  handleQuerySubmit,
  handleUpload,
  min,
  max,
}) => {
  const getFormComponent = () => {
    if (setValue != null) {
      switch (type) {
        case "text":
        case "email":
        case "number":
          return (
            <Input
              type={type}
              name={name}
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              min={min}
              max={max}
            />
          );
        case "mobile":
          return (
            <MobileInput
              value={value}
              setter={setValue}
              className="form-element-mobile"
            />
          );
        case "textarea":
          return (
            <Textarea
              name={name}
              value={value}
              setValue={setValue}
              placeholder={placeholder}
            />
          );
        case "select":
          return (
            <Select
              options={options}
              name={name}
              value={value}
              setValue={setValue}
              placeholder={placeholder}
            />
          );
        case "radio":
          return (
            <RadioButtons
              name={name}
              options={options}
              value={value}
              setValue={setValue}
            />
          );
        case "checkbox":
          return (
            <Checkboxes
              name={name}
              options={options}
              value={value}
              setValue={setValue}
            />
          );
        case "wysiwyg":
          return (
            <WSYIWYGEditor
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              handleUpload={handleUpload}
            />
          );
        case "date":
          return (
            <DateTimePicker
              value={value}
              setValue={setValue}
              name={name}
              hideTime
              placeholder={placeholder}
              isValidDate={isValidDate}
            />
          );
        case "time":
          return (
            <DateTimePicker
              value={value}
              setValue={setValue}
              name={name}
              hideDate
              placeholder={placeholder}
            />
          );
        case "datetime":
          return (
            <DateTimePicker
              value={value}
              setValue={setValue}
              name={name}
              placeholder={placeholder}
            />
          );
        case "file-upload":
          return (
            <FileUpload
              value={value}
              setValue={setValue}
              fileAccept={fileAccept}
              name={name}
              placeholder={placeholder}
            />
          );
        case "multi-select":
          return (
            <MultiSelect
              options={options}
              name={name}
              value={value}
              setValue={setValue}
              placeholder={placeholder}
              isSortable={isSortable}
              hideSelected={hideSelected}
              handleQuerySubmit={handleQuerySubmit}
            />
          );
        case "toggle":
          return (
            <ToggleSwitch
              name={name}
              checked={Boolean(value)}
              onChange={(checked) => {
                setValue(checked);
              }}
              disabled={disabled}
            />
          );
        case "map":
          return (
            <DrawMap
              center={map.options?.center}
              zoom={map.options?.zoom}
              mapStyle={map.options?.style}
              drawControls={map.drawControls}
              value={value}
              setValue={setValue}
            />
          );
        case "country-selector":
          return <CountrySelector country={value} setCountry={setValue} />;
        default:
          break;
      }
    }
  };

  return (
    <Flex
      className={`${type === "map" && map.heightClass} ${
        type === "map" && map.widthClass
      } sm:flex-row flex-col`.concat(" ", className)}
    >
      <Flex
        col
        justify={labelTopAligned ? "start" : "center"}
        className={`${labelWidthClass || "w-56"} ${
          labelTopAligned ? "pt-3" : ""
        } flex-shrink-0 flex-grow-0 mb-1 sm:mr-2 sm:mb-0`}
      >
        <label
          className="pl-2 text-sm font-medium text-text sm:pl-0"
          htmlFor={name}
        >
          {label}
          {required && (
            <sup
              className="text-lg text-orange"
              style={{ top: "-0.1rem", left: "0.1rem" }}
            >
              *
            </sup>
          )}
        </label>
        {hint && (
          <span className="text-xs font-medium break-normal text-subtitle">
            {hint}
          </span>
        )}
      </Flex>
      <Flex
        className={`flex-grow flex-wrap ${
          disabled ? "pointer-events-none" : ""
        }`}
      >
        {children || getFormComponent()}
      </Flex>
    </Flex>
  );
};

export default FormElement;
